import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

/**
 * Marks that this browsing session has already burned its one automatic reload
 * on a failed chunk load. Shared with `lazyRetry` in App.tsx so the two paths
 * can't each spend a reload and turn one failure into a reload loop.
 */
export const CHUNK_RELOAD_KEY = 'chunk-reload';

/** Reload once per session; returns false if the reload was already spent. */
export function reloadOnceForChunkFailure(): boolean {
  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
  } catch {
    // Private browsing denies sessionStorage. With nowhere to record the
    // attempt there's no way to stop a second one, so don't start a first.
    return false;
  }
  window.location.reload();
  return true;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });

    // Auto-reload once on chunk load failures (stale cache after deployment)
    if (
      error.message?.includes('dynamically imported module') ||
      error.message?.includes('Failed to fetch')
    ) {
      reloadOnceForChunkFailure();
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div role="alert" className="min-h-[400px] flex items-center justify-center p-8">
          <div className="max-w-md w-full text-center space-y-4">
            <div className="mx-auto w-14 h-14 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-red-500" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Something went wrong</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>
            {this.state.error?.stack && (
              <pre className="mt-3 p-3 bg-slate-100 dark:bg-slate-700 rounded-lg text-left text-xs text-slate-600 dark:text-slate-300 max-h-48 overflow-auto whitespace-pre-wrap">
                {this.state.error.stack}
              </pre>
            )}
            {this.state.componentStack && (
              <details className="mt-2 text-left">
                <summary className="text-xs text-slate-400 dark:text-slate-500 cursor-pointer">Component Stack</summary>
                <pre className="mt-1 p-3 bg-slate-100 dark:bg-slate-700 rounded-lg text-xs text-slate-600 dark:text-slate-300 max-h-48 overflow-auto whitespace-pre-wrap">
                  {this.state.componentStack}
                </pre>
              </details>
            )}
            <button
              onClick={() => {
                const isChunkError = this.state.error?.message?.includes('dynamically imported module') ||
                  this.state.error?.message?.includes('Failed to fetch');
                if (isChunkError) {
                  window.location.reload();
                } else {
                  this.setState({ hasError: false, error: null, componentStack: null });
                }
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-fy-primary text-white text-sm font-medium hover:bg-fy-primary-dark transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Try again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
